<script>
import moment from "moment";
import axios from '@/utils/axios';
import "daterangepicker/daterangepicker.js";
import "daterangepicker/daterangepicker.css";
import DateRangePicker from "daterangepicker";
import Vue3ChartJs from "@j-t-mcc/vue3-chartjs";
import { clientAdmin } from "@/utils/auth";

export default {
  inject: ['setLoading'],
  components: { Vue3ChartJs },
  data() {
    return {
      errors: {},
      title: "Reports",
      text: "Inventory",
      text1: "Production Report",
      clientAdmin: clientAdmin(),
      filter: {
        from_date: '',
        to_date: '',
        warehouse_id: '',
        item_id: '',
      },
      summary: {},
      data: [],
      warehouses: [],
      items: [],
      pagination: {
        current_page: 1,
        per_page: 25,
        last_page: 1,
        total: 0,
        links: []
      },
     modalRow: null,
     modalType: 'finished',
    };
  },

  computed: {
    serialNo() {
      return (this.pagination.current_page - 1) * this.pagination.per_page;
    },

    finishedGoodsBarChart() {
      const map = {};
      (this.data || []).forEach(row => {
        (row.items || []).forEach(i => {
          const name = i.item?.name || 'Unknown';
          map[name] = (map[name] || 0) + parseFloat(i.quantity || 0);
        });
      });
      const labels = Object.keys(map);
      const values = Object.values(map);
      const colors = ['#4f46e5', '#06b6d4', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];
      return {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: 'Finished Qty',
            data: values,
            backgroundColor: labels.map((_, i) => colors[i % colors.length]),
            borderRadius: 6,
            barThickness: 40,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            y: { beginAtZero: true, grid: { color: '#f3f4f6' } },
            x: { grid: { display: false } }
          }
        }
      };
    },

    wastageBarChart() {
      const map = {};
      (this.data || []).forEach(row => {
        (row.wastages || []).forEach(w => {
          const name = w.item?.name || 'Unknown';
          map[name] = (map[name] || 0) + parseFloat(w.quantity || 0);
        });
      });
      const labels = Object.keys(map);
      const values = Object.values(map);
      const colors = ['#ef4444', '#f97316', '#f59e0b', '#dc2626', '#b91c1c', '#fca5a5'];
      return {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: 'Wastage Qty',
            data: values,
            backgroundColor: labels.map((_, i) => colors[i % colors.length]),
            borderRadius: 6,
            barThickness: 40,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            y: { beginAtZero: true, grid: { color: '#f3f4f6' } },
            x: { grid: { display: false } }
          }
        }
      };
    },

    finishedVsWastageDoughnut() {
      const finished = parseFloat(this.summary.total_finished_qty || 0);
      const wastage  = parseFloat(this.summary.total_wastage_qty  || 0);
      return {
        type: 'doughnut',
        data: {
          labels: ['Finished Goods', 'Wastage'],
          datasets: [{
            data: [finished, wastage],
            backgroundColor: ['#10b981', '#ef4444'],
            borderWidth: 2,
            hoverOffset: 6,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom', labels: { padding: 15, usePointStyle: true, pointStyle: 'circle' } }
          },
          cutout: '65%',
        }
      };
    },
  },

  async mounted() {
    this.getDropdowns();

    const start = moment().subtract(6, "days");
    const end   = moment();

    this.filter.from_date = start.format("YYYY-MM-DD");
    this.filter.to_date   = end.format("YYYY-MM-DD");

    new DateRangePicker(
      this.$refs.dateRangeInput,
      {
        startDate: start,
        endDate: end,
        autoUpdateInput: false,
        locale: { format: "DD/MM/YYYY", separator: " - ", cancelLabel: "Clear" },
        ranges: {
          Today:          [moment(), moment()],
          Yesterday:      [moment().subtract(1, "days"), moment().subtract(1, "days")],
          "Last 7 Days":  [moment().subtract(6, "days"), moment()],
          "Last 30 Days": [moment().subtract(29, "days"), moment()],
          "This Month":   [moment().startOf("month"), moment().endOf("month")],
          "Last Month":   [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "month").endOf("month")],
        }
      },
      (start, end) => {
        this.filter.from_date = start.format("YYYY-MM-DD");
        this.filter.to_date   = end.format("YYYY-MM-DD");
        this.$refs.dateRangeInput.value = start.format("DD/MM/YYYY") + " - " + end.format("DD/MM/YYYY");
        this.filterReport();
      }
    );

    this.filterReport();
  },

  methods: {

    openItemsModal(row, type) {
    this.modalRow = row;
    this.modalType = type;
    this.$openModal('production_items_modal');
    },

    async exportCSV() {
      this.setLoading(true);
      try {
        const payload = { export: 'csv' };
        if (this.filter.from_date)   payload.date_from    = this.filter.from_date;
        if (this.filter.to_date)     payload.date_to      = this.filter.to_date;
        if (this.filter.warehouse_id) payload.warehouse_id = this.filter.warehouse_id;
        if (this.filter.item_id)     payload.item_id      = this.filter.item_id;

        const qs = new URLSearchParams(payload).toString();
        const response = await axios.get(`/reports/production?${qs}`, { responseType: 'blob' });

        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `production-report-${this.filter.from_date}-to-${this.filter.to_date}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        this.$swalToast('Failed to export CSV', 'error');
      } finally {
        this.setLoading(false);
      }
    },

    async filterReport(page = 1) {
      this.errors = {};
      this.setLoading(true);
      try {
        const payload = {
          page,
          date_from: this.filter.from_date,
          date_to:   this.filter.to_date,
        };
        if (this.filter.warehouse_id) payload.warehouse_id = this.filter.warehouse_id;
        if (this.filter.item_id)      payload.item_id      = this.filter.item_id;

        const qs = new URLSearchParams(payload).toString();
        const response = await axios.get(`/reports/production?${qs}`);

        this.summary = response.data.data.summary || {};
        this.data    = response.data.data.rows.data;
        this.pagination = {
          current_page: response.data.data.rows.current_page,
          per_page:     response.data.data.rows.per_page,
          last_page:    response.data.data.rows.last_page,
          total:        response.data.data.rows.total,
          links:        response.data.data.rows.links,
        };
      } catch (error) {
        if (error.type == 'validation') {
          this.errors = error.errors;
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },

    async getDropdowns() {
      try {
        const [warehouseRes, itemRes] = await Promise.all([
          axios.get('/inventory/warehouses/dropdown'),
          axios.get('/items/dropdown'),
        ]);
        this.warehouses = warehouseRes.data.data;
        this.items      = itemRes.data.data;
      } catch (error) {
        console.log('Dropdown load error:', error.message);
      }
    },

    changePage(url) {
      if (!url) return;
      const page = new URL(url).searchParams.get("page");
      this.filterReport(page);
    },

    hasActiveFilter() {
      return this.filter.warehouse_id || this.filter.item_id || this.filter.from_date || this.filter.to_date;
    },

    clearFilter() {
      this.filter.warehouse_id = '';
      this.filter.item_id      = '';
      this.filter.from_date    = '';
      this.filter.to_date      = '';
      this.$refs.dateRangeInput.value = '';
      this.filterReport();
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
        + ', ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    },

    formatQty(val) {
      const num = parseFloat(val || 0);
      return num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);
    },
  }
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Breadcrumb -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <button class="btn btn-outline-primary d-flex align-items-center" @click="exportCSV" :disabled="!data || !data.length">
            <i class="ti ti-file-type-csv me-2"></i>Export CSV
          </button>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="row g-3 mb-3" v-if="summary && summary.total_productions">
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Total Productions</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ summary.total_productions }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Total DC Used</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ summary.total_dc_used }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Finished Qty</span>
              <span class="fw-bold text-success" style="font-size:20px;">{{ formatQty(summary.total_finished_qty) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Wastage Qty</span>
              <span class="fw-bold text-danger" style="font-size:20px;">{{ formatQty(summary.total_wastage_qty) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Unique Finished Goods</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ summary.unique_finished_goods }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Unique Wastage Items</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ summary.unique_wastage_items }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts -->
      <div class="row g-3 mb-3" v-if="data && data.length">
        <div class="col-md-5">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-header border-0 pb-0">
              <h6 class="fw-bold mb-0">
                <i class="ti ti-package me-1 text-success"></i>Finished Goods - Item wise Qty
              </h6>
            </div>
            <div class="card-body" style="height:280px;">
              <vue3-chart-js v-bind="finishedGoodsBarChart" :key="'fg-'+data.length" />
            </div>
          </div>
        </div>
        <div class="col-md-5">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-header border-0 pb-0">
              <h6 class="fw-bold mb-0">
                <i class="ti ti-trash me-1 text-danger"></i>Wastage - Item wise Qty
              </h6>
            </div>
            <div class="card-body" style="height:280px;">
              <vue3-chart-js v-bind="wastageBarChart" :key="'wg-'+data.length" />
            </div>
          </div>
        </div>
        <div class="col-md-2">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-header border-0 pb-0">
              <h6 class="fw-bold mb-0">
                <i class="ti ti-chart-donut me-1 text-primary"></i>Finished vs Wastage
              </h6>
            </div>
            <div class="card-body" style="height:280px;">
              <vue3-chart-js v-bind="finishedVsWastageDoughnut" :key="'fvw-'+data.length" />
            </div>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="card border-0">
        <div class="card-body d-flex align-items-center justify-content-center flex-wrap pb-1">
          <div class="d-flex align-items-center flex-wrap mb-1 gap-2">
            <div class="me-3" v-if="hasActiveFilter()">
              <a href="javascript:void(0);" class="text-danger" @click="clearFilter()">
                <i class="ti ti-filter-x"></i>
              </a>
            </div>
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input type="text" class="form-control date-range bookingrange" ref="dateRangeInput" placeholder="dd/mm/yyyy - dd/mm/yyyy">
                <span class="input-icon-addon"><i class="ti ti-chevron-down"></i></span>
              </div>
            </div>
            <div class="me-3">
              <vue-select
                v-model="filter.warehouse_id"
                :options="warehouses"
                label="name"
                :reduce="w => w.id"
                placeholder="Warehouse"
                :clearable="true"
                @update:modelValue="filterReport()"
                style="min-width:180px;"
              />
            </div>
            <div class="me-3">
              <vue-select
                v-model="filter.item_id"
                :options="items"
                label="name"
                :reduce="i => i.id"
                placeholder="Item"
                :clearable="true"
                @update:modelValue="filterReport()"
                style="min-width:180px;"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="row">
        <div class="col-lg-12">
          <div class="card-body card-table-wrapper">
            <div class="custom-datatable-filter table-responsive">
              <table class="card-table" v-if="data && data.length">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Challan No</th>
                    <th>Finished Goods</th>
                    <th>Wastage Items</th>
                    <th>Created By</th>
                    <th>Remark</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in data" :key="row.id">

                    <td>{{ serialNo + index + 1 }}.</td>

                    <td>
                      <span>{{ formatDate(row.created_at) }}</span>
                      <small class="text-muted d-block" style="font-size:10px;">
                        {{ formatDateTime(row.created_at).split(', ')[1] }}
                      </small>
                    </td>

                    <td>
                      <span class="badge bg-light text-primary border rounded-pill">
                        {{ row.delivery_challan?.challan_number || '-' }}
                      </span>
                    </td>

                    <!-- Finished Goods column -->
                    <td>
                    <span
                        v-if="row.items && row.items.length"
                        class="badge bg-success-subtle text-success border border-success-subtle"
                        style="cursor:pointer; font-size:11px; padding:5px 10px; border-radius:20px;"
                        @click="openItemsModal(row, 'finished')"
                    >
                        <i class="ti ti-package me-1"></i>{{ row.items.length }} item{{ row.items.length > 1 ? 's' : '' }}
                    </span>
                    <span v-else class="text-muted" style="font-size:11px;">-</span>
                    </td>

                    <!-- Wastage Items column -->
                    <td>
                    <span
                        v-if="row.wastages && row.wastages.length"
                        class="badge bg-danger-subtle text-danger border border-danger-subtle"
                        style="cursor:pointer; font-size:11px; padding:5px 10px; border-radius:20px;"
                        @click="openItemsModal(row, 'wastage')"
                    >
                        <i class="ti ti-trash me-1"></i>{{ row.wastages.length }} item{{ row.wastages.length > 1 ? 's' : '' }}
                    </span>
                    <span v-else class="text-success" style="font-size:11px;">
                        <i class="ti ti-check me-1"></i>No wastage
                    </span>
                    </td>

                    <td>
                      <small class="text-muted">{{ row.user?.name || '-' }}</small>
                    </td>

                    <td>
                      <small class="text-muted">{{ row.remark || '-' }}</small>
                    </td>

                  </tr>
                </tbody>
              </table>
              <div v-else class="p-3 text-center text-muted">No records found.</div>

              <!-- Pagination -->
              <nav v-if="pagination.links && pagination.links.length > 3" class="m-3">
                <ul class="pagination pagination-sm">
                  <li
                    v-for="(link, index) in pagination.links"
                    :key="index"
                    :class="['page-item', { active: link.active, disabled: !link.url }]"
                  >
                    <a class="page-link" href="javascript:void(0)" v-html="link.label" @click="changePage(link.url)"></a>
                  </li>
                </ul>
              </nav>

            </div>
          </div>
        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>



  <!-- Items Detail Modal -->
<div class="modal fade" id="production_items_modal" tabindex="-1">
  <div class="modal-dialog modal-dialog-centered">
    <div class="modal-content border-0" style="border-radius:14px; overflow:hidden;">
      
      <div class="modal-header border-0 pb-0 px-4 pt-4"
        :style="modalType === 'finished' 
          ? 'background: linear-gradient(135deg, #f0fdf4, #dcfce7);' 
          : 'background: linear-gradient(135deg, #fff1f2, #fee2e2);'">
        <div>
          <h5 class="modal-title fw-bold mb-1" style="font-size:15px;">
            <i class="ti me-2" 
              :class="modalType === 'finished' ? 'ti-package text-success' : 'ti-trash text-danger'">
            </i>
            {{ modalType === 'finished' ? 'Finished Goods' : 'Wastage Items' }}
          </h5>
          <small class="text-muted" v-if="modalRow">
            {{ modalRow.delivery_challan?.challan_number }}
          </small>
        </div>
        <button type="button" class="btn-close ms-auto" data-bs-dismiss="modal"></button>
      </div>

      <div class="modal-body px-4 py-3">
        <div v-if="modalType === 'finished' && modalRow">
          <div
            v-for="(item, i) in modalRow.items" :key="item.id"
            class="d-flex align-items-center gap-3 p-3 mb-2"
            style="background:#f8faff; border-radius:10px; border:1px solid #e8edf3;"
          >
            <div style="width:36px; height:36px; background:#dcfce7; border-radius:8px;
              display:flex; align-items:center; justify-content:center; flex-shrink:0;">
              <i class="ti ti-package text-success" style="font-size:16px;"></i>
            </div>
            <div class="flex-grow-1">
              <div class="fw-semibold" style="font-size:13px; color:#0f172a;">{{ item.item?.name }}</div>
              <div class="text-muted" style="font-size:11px;">Code: {{ item.item?.code }}</div>
            </div>
            <div class="text-end">
              <span class="badge bg-success-subtle text-success" style="font-size:12px; padding:4px 10px;">
                {{ formatQty(item.quantity) }} qty
              </span>
            </div>
          </div>
        </div>

        <div v-if="modalType === 'wastage' && modalRow">
          <div
            v-for="(w, i) in modalRow.wastages" :key="w.id"
            class="d-flex align-items-center gap-3 p-3 mb-2"
            style="background:#fff8f8; border-radius:10px; border:1px solid #fee2e2;"
          >
            <div style="width:36px; height:36px; background:#fee2e2; border-radius:8px;
              display:flex; align-items:center; justify-content:center; flex-shrink:0;">
              <i class="ti ti-trash text-danger" style="font-size:16px;"></i>
            </div>
            <div class="flex-grow-1">
              <div class="fw-semibold" style="font-size:13px; color:#0f172a;">{{ w.item?.name }}</div>
              <div class="text-muted" style="font-size:11px;">
                Code: {{ w.item?.code }}
                <span v-if="w.remark" class="ms-2">• {{ w.remark }}</span>
              </div>
            </div>
            <div class="text-end">
              <span class="badge bg-danger-subtle text-danger" style="font-size:12px; padding:4px 10px;">
                {{ formatQty(w.quantity) }} qty
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer border-0 pt-0">
        <button type="button" class="btn btn-light btn-sm" data-bs-dismiss="modal">Close</button>
      </div>

    </div>
  </div>
</div>
</template>

<style scoped>
.table-responsive {
  overflow-y: auto;
}
</style>